import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { LogOut, User, Trophy, ListChecks, ShieldCheck, ClipboardList } from "lucide-react";
import { useAuth, useUserProfile } from "@/lib/AuthContext";
import { usePendingReviewsCount } from "@/api/hooks";
import NotificationBell from "@/components/notifications/NotificationBell";
import ProfileAvatar from "@/components/layout/ProfileAvatar";

const NAV_ITEMS = [
  { path: "/tasks", label: "המשימות שלי", icon: ListChecks },
  { path: "/leaderboard", label: "לידרבורד", icon: Trophy },
  { path: "/profile", label: "פרופיל", icon: User },
];

const ADMIN_ITEMS = [
  { path: "/admin", label: "ניהול", icon: ShieldCheck },
  { path: "/admin/queue", label: "תור בדיקה", icon: ClipboardList, badgeKey: "pending_reviews" },
];

export default function AppHeader() {
  const { isAuthenticated: loggedIn, isAdmin, displayName, email } = useUserProfile();
  const { data: pendingData } = usePendingReviewsCount({ enabled: isAdmin });
  const pendingReviews = pendingData?.count ?? 0;
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const user = loggedIn ? { full_name: displayName, email } : null;

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 w-full"
      style={{ background: "#000", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
      dir="rtl"
    >
      <div className="max-w-5xl mx-auto w-full px-4 py-2.5 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img
            src="https://media.base44.com/images/public/69b41710d6b60acb7a810a52/fd7ecff3f_momentum_logo.png"
            alt="MOM3NTUM"
            className="h-5 w-auto object-contain"
          />
        </Link>

        {/* Center nav — desktop */}
        {loggedIn && (
          <nav className="hidden md:flex items-center gap-1 overflow-hidden">
            {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap"
                style={{
                  color: isActive(path) ? "#c8f135" : "rgba(255,255,255,0.5)",
                  background: isActive(path) ? "rgba(200,241,53,0.1)" : "transparent",
                }}
              >
                <Icon size={14} />
                {label}
              </Link>
            ))}
            {isAdmin && ADMIN_ITEMS.map(({ path, label, icon: Icon, badgeKey }) => {
              const badgeCount = badgeKey === "pending_reviews" ? pendingReviews : 0;
              return (
                <Link
                  key={path}
                  to={path}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap"
                  style={{
                    color: isActive(path) ? "#c8f135" : "rgba(255,255,255,0.35)",
                    background: isActive(path) ? "rgba(200,241,53,0.1)" : "transparent",
                  }}
                >
                  <Icon size={14} />
                  {label}
                  {badgeCount > 0 && (
                    <span
                      className="text-[10px] font-black px-1.5 py-0.5 rounded-full"
                      style={{ background: "#ff5d68", color: "#000", minWidth: "1.25rem", textAlign: "center" }}
                    >
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        )}

        {/* Right side: notifications + user — isolated z-index to prevent overlap */}
        <div className="flex items-center gap-3 flex-shrink-0" style={{ position: "relative", zIndex: 60 }}>
          {loggedIn && user ? (
            <>
              <NotificationBell />
              <div className="relative" ref={menuRef}>
                <ProfileAvatar
                  user={{ displayName, email }}
                  onClick={() => setMenuOpen((o) => !o)}
                />

                {menuOpen && (
                  <div
                    className="absolute left-0 top-11 rounded-2xl py-1 min-w-[200px] z-50"
                    style={{ background: "#111", border: "1px solid #2a2a2a", boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }}
                  >
                    {/* User info */}
                    <div className="px-4 py-3" style={{ borderBottom: "1px solid #1f1f1f" }}>
                      <p className="text-xs font-black text-white truncate">{user.full_name || user.email}</p>
                      <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.35)" }}>{user.email}</p>
                    </div>

                    {/* Mobile nav items (hidden on desktop) */}
                    <div className="md:hidden" style={{ borderBottom: "1px solid #1f1f1f" }}>
                      {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
                        <button
                          key={path}
                          onClick={() => { setMenuOpen(false); navigate(path); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-bold transition-colors hover:bg-white/5"
                          style={{ color: isActive(path) ? "#c8f135" : "rgba(255,255,255,0.7)" }}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {label}
                        </button>
                      ))}
                      {isAdmin && ADMIN_ITEMS.map(({ path, label, icon: Icon, badgeKey }) => {
                        const badgeCount = badgeKey === "pending_reviews" ? pendingReviews : 0;
                        return (
                          <button
                            key={path}
                            onClick={() => { setMenuOpen(false); navigate(path); }}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-bold transition-colors hover:bg-white/5"
                            style={{ color: isActive(path) ? "#c8f135" : "rgba(255,255,255,0.5)" }}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {label}
                            {badgeCount > 0 && (
                              <span
                                className="text-[10px] font-black px-1.5 py-0.5 rounded-full ms-auto"
                                style={{ background: "#ff5d68", color: "#000" }}
                              >
                                {badgeCount > 99 ? "99+" : badgeCount}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Logout */}
                    <button
                      onClick={() => logout()}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-bold transition-colors hover:bg-white/5"
                      style={{ color: "rgba(255,80,80,0.85)" }}
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      יציאה
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <ProfileAvatar
              user={null}
              onClick={() => { window.location.href = "/account/login"; }}
            />
          )}
        </div>
      </div>
    </header>
  );
}
